import { useState } from "react";
import {
  FileSearch, CloudSun, ShieldCheck, GraduationCap, Newspaper,
  Sparkles, Rocket, ExternalLink, ChevronDown, Check, Info,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const ICONS = { FileSearch, CloudSun, ShieldCheck, GraduationCap, Newspaper, Sparkles, Rocket };

const statusStyles = {
  Live: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "In Development": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "In Testing": "bg-violet-500/15 text-violet-300 border-violet-500/30",
  Preview: "bg-sky-500/15 text-sky-300 border-sky-500/30",
};

const PlayIcon = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
    <path d="M3.6 2.4c-.3.3-.5.7-.5 1.3v16.6c0 .6.2 1 .5 1.3l.1.1L13 12.1v-.2L3.7 2.3l-.1.1zM16.3 15.4l-2.8-2.8v-.2l2.8-2.8 3.3 1.9c.9.5.9 1.5 0 2l-3.3 1.9zM13.5 12.5l2.5 2.5-9.9 5.7c-.5.3-1 .3-1.4 0l8.8-8.2zM6.1 3.2c.4-.2.9-.2 1.4 0l9.9 5.7-2.5 2.5-8.8-8.2z" />
  </svg>
);

export const ProjectCard = ({ project }) => {
  const [open, setOpen] = useState(false);
  const Icon = ICONS[project.icon] || Rocket;
  const isDev = project.status === "In Development";

  return (
    <article
      data-testid={`project-card-${project.id}`}
      className="ht-card group flex flex-col overflow-hidden rounded-xl"
    >
      <div
        className={`relative flex h-32 items-center justify-between overflow-hidden bg-gradient-to-br ${project.accent} px-6`}
      >
        <div className="absolute inset-0 ht-grid-lines opacity-40" />
        {project.iconImage ? (
          <img
            src={project.iconImage}
            alt={project.iconAlt || `${project.name} app icon`}
            loading="lazy"
            className="relative h-14 w-14 rounded-xl border border-white/10 bg-[#040B16]/70 object-cover backdrop-blur"
          />
        ) : (
          <span className="relative flex h-14 w-14 items-center justify-center rounded-xl border border-white/10 bg-[#040B16]/70 text-sky-300 backdrop-blur">
            <Icon size={26} />
          </span>
        )}
        {project.status && (
          <Badge
            variant="outline"
            data-testid={`project-status-${project.id}`}
            className={`relative rounded-full border px-3 py-1 text-xs font-medium ${statusStyles[project.status] || ""}`}
          >
            {project.status}
          </Badge>
        )}
      </div>

      <div className="flex flex-1 flex-col p-6">
        <p className="text-xs font-medium uppercase tracking-wider text-sky-400">
          {project.category}
        </p>
        <h3 className="mt-1.5 font-display text-xl font-semibold text-white">
          {project.name}
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">
          {project.description}
        </p>

        {project.capabilities && (
          <>
            <button
              data-testid={`project-expand-${project.id}`}
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              className="mt-4 flex items-center gap-1.5 text-sm font-medium text-sky-300 transition-colors hover:text-sky-200"
            >
              {open ? "Hide capabilities" : "Key capabilities"}
              <ChevronDown
                size={16}
                className="transition-transform duration-200"
                style={{ transform: open ? "rotate(180deg)" : "none" }}
              />
            </button>

            {open && (
              <div data-testid={`project-detail-${project.id}`} className="mt-3">
                <ul className="space-y-2">
                  {project.capabilities.map((c) => (
                    <li key={c} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check size={15} className="mt-0.5 shrink-0 text-teal-400" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
                {project.disclaimer && (
                  <p className="mt-4 flex items-start gap-2 rounded-md border border-slate-800 bg-[#040B16]/60 p-3 text-xs leading-relaxed text-slate-400">
                    <Info size={14} className="mt-0.5 shrink-0 text-slate-500" />
                    <span>{project.disclaimer}</span>
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {(project.website || project.playStore || isDev) && (
          <div className="mt-6 flex flex-wrap gap-3 pt-2">
            {project.website && (
              <Button
                asChild
                size="sm"
                data-testid={`project-website-${project.id}`}
                className="rounded-full bg-sky-500 text-[#040B16] hover:bg-sky-400"
              >
                <a
                  href={project.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={project.websiteLabel || `Visit the ${project.name} website`}
                >
                  Visit Website <ExternalLink size={14} className="ml-1.5" />
                </a>
              </Button>
            )}
            {project.playStore && (
              <Button
                asChild
                size="sm"
                variant="outline"
                data-testid={`project-playstore-${project.id}`}
                className="rounded-full border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white"
              >
                <a href={project.playStore} target="_blank" rel="noopener noreferrer">
                  <PlayIcon className="mr-1.5" /> Google Play
                </a>
              </Button>
            )}
            {isDev && (
              <span className="text-sm text-slate-500">
                New products are announced here as they launch.
              </span>
            )}
          </div>
        )}
      </div>
    </article>
  );
};
